import { useMemo } from 'react';

import { getYear } from 'date-fns';

import { useLocale } from '@adsk/alloy-react-locale';

import {
  getWeekDaysShort,
  getMonths,
  convertYearRangeToArray,
  getFirstDayOfWeek,
} from '../helpers';
import { TTranslate } from '../helpers/types';
import { DatePickerDate } from '../types';

const monthsOfTheYear = (T: TTranslate) => [
  { key: 0, label: T.translate('JANUARY') },
  { key: 1, label: T.translate('FEBRUARY') },
  { key: 2, label: T.translate('MARCH') },
  { key: 3, label: T.translate('APRIL') },
  { key: 4, label: T.translate('MAY') },
  { key: 5, label: T.translate('JUNE') },
  { key: 6, label: T.translate('JULY') },
  { key: 7, label: T.translate('AUGUST') },
  { key: 8, label: T.translate('SEPTEMBER') },
  { key: 9, label: T.translate('OCTOBER') },
  { key: 10, label: T.translate('NOVEMBER') },
  { key: 11, label: T.translate('DECEMBER') },
];

function useCalendars({
  activeMonths,
  selectedDates,
  minDate,
  maxDate,
  firstDayOfWeek: firstDayOfWeekProp,
  showOutsideDays,
  isDateSelectable,
  yearsToDisplay,
  timeZone,
}: {
  activeMonths: { year: number; month: number }[];
  selectedDates: (DatePickerDate | null)[] | null;
  minDate?: Date | null;
  maxDate?: Date | null;
  firstDayOfWeek?: number;
  showOutsideDays?: boolean;
  isDateSelectable?: ({ date }: { date: Date }) => boolean;
  yearsToDisplay: number;
  timeZone?: string;
}) {
  const { T, locale } = useLocale(({ locale: loc }) =>
    require(`../i18n/${loc}.json`),
  );
  const firstDayOfWeek = getFirstDayOfWeek(locale, firstDayOfWeekProp);
  const weekdays = useMemo(
    () => getWeekDaysShort(T, firstDayOfWeek),
    [T, firstDayOfWeek],
  );

  return useMemo(
    () =>
      activeMonths.map((month, index) => {
        const calendar = getMonths({
          month: month.month,
          year: month.year,
          selectedDates,
          minDate,
          maxDate,
          firstDayOfWeek,
          showOutsideDays,
          isDateSelectable,
          timeZone,
        });

        const months = monthsOfTheYear(T);

        const years = convertYearRangeToArray({
          min: minDate ? getYear(minDate) : month.year - yearsToDisplay,
          max: maxDate ? getYear(maxDate) : month.year + yearsToDisplay,
          selectedDates,
        });

        return {
          ...calendar,
          index,
          months,
          weekdays,
          years,
          selectedMonth: months.find(({ key }) => key === calendar.month),
          selectedYear: years.find(({ key }) => key === calendar.year),
        };
      }),
    [
      T,
      firstDayOfWeek,
      isDateSelectable,
      showOutsideDays,
      weekdays,
      yearsToDisplay,
      activeMonths,
      minDate,
      maxDate,
      selectedDates,
      timeZone,
    ],
  );
}

export default useCalendars;
